import i18next from 'i18next'
import React from 'react'
import { Link } from '../../utils/links'
import { ViewListenerState } from '../viewListener/viewListenerState'
import { TranslateState } from '../automaticTranslator/translateState'
import { TranslateBanner } from '../automaticTranslator/translateBanner'
import {
  recommendedLanguages,
  setTranslationBannerCookie,
  deleteGoogleCookie,
  customTranslations,
  translateDataLayerEvent,
} from '../automaticTranslator/translateUtils'

// ----- Skiplinks

export const SkipLinks = () => (
  <>
    <a href="#a11y-main" className="sr-text">
      {i18next.t('a11y:skipToContent')}
    </a>
    <a href="#a11y-footer" className="sr-text">
      {i18next.t('a11y:skipToFooter')}
    </a>
  </>
)

// ----- Header Layout

type NavItem = {
  title: string
  url: string
  children: {
    title: string
    url: string
  }[]
}

type UtilityItem = {
  title: string
  url: string
}

export const HeaderLayout = (props: {
  items: NavItem[]
  utilityItems: UtilityItem[]
  isMenuOpen: boolean
  toggleMenu: () => void
  view: ViewListenerState
  translateState: TranslateState
  toggleTranslateModal: (toggle: boolean) => void
  closeTranslateBanner: () => void
  updateSelectedLanguage: () => void
}) => {
  const { items: items, utilityItems: utilityItems } = props
  const lastButton = items[items.length - 1]
  const isLinkActive = (url: string) => url === window.location.pathname.toLocaleLowerCase()

  React.useEffect(() => {
    const script = document.createElement('script')
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
    script.async = true
    document.body.appendChild(script)
    ;(window as any).googleTranslateElementInit = () => {
      new (window as any).google.translate.TranslateElement({ autoDisplay: false }, 'google_translate_element')
      const languageSelectElement = document.querySelector('#\\:0\\.targetLanguage select') as HTMLSelectElement
      if (languageSelectElement) {
        languageSelectElement.classList.add('notranslate')
        languageSelectElement.id = 'google_language_selector'
        languageSelectElement.addEventListener('change', function () {
          translateDataLayerEvent(languageSelectElement.value)
          setTranslationBannerCookie('yes')
          customTranslations()
          props.updateSelectedLanguage()
        })
      }
    }
  }, [])

  const selectLanguage = (code: string) => {
    if (code === 'nl') {
      setTranslationBannerCookie('no')
      props.closeTranslateBanner()
      deleteGoogleCookie('googtrans')
      window.location.reload()
    } else {
      const selectElement = document.getElementById('google_language_selector') as HTMLSelectElement
      if (selectElement) {
        selectElement.value = code
        selectElement.dispatchEvent(new Event('change'))
      }
    }
  }
  const isSubMenuActive = (item: NavItem): boolean =>
    item.children.filter((c) => c.url).find((c) => c.url === window.location.pathname.toLocaleLowerCase())
      ? true
      : false

  return (
    <>
      <SkipLinks />
      <header className="header">
        <div className="header__inner">
          <div className="mainmenu">
            <div className="mainmenu__inner container--l gutter--m">
              <nav
                className="menu menu--main"
                aria-label="Main navigation"
                onMouseLeave={() => {
                  ;(document.documentElement.classList.remove('submenu--open'),
                    document.querySelector('.hovered')?.classList.remove('hovered'))
                }}
              >
                <Link url="/" className="logo">
                  <span className="sr-text">Return to homepage</span>
                  <img
                    className="logo__image"
                    src="/images/evo-logo.svg"
                    width="205"
                    height="29"
                    alt=""
                    aria-hidden="true"
                  />
                </Link>

                <ul className="menu__list">
                  {items.slice(0, items.length - 1).map((item) => (
                    <li className="menu__item" key={`${item.url}-${item.title}`}>
                      <Link
                        url={item.url}
                        className={`menu__link${isLinkActive(item.url) || isSubMenuActive(item) ? ' active' : ''}`}
                        onMouseEnter={(e) =>
                          item.children.length > 0
                            ? (document.documentElement.classList.add('submenu--open'),
                              document.querySelector('.hovered')?.classList.remove('hovered'),
                              e.currentTarget.classList.add('hovered'))
                            : (document.documentElement.classList.remove('submenu--open'),
                              document.querySelector('.hovered')?.classList.remove('hovered'))
                        }
                        // onMouseLeave={e => {
                        //   document.documentElement.classList.remove(
                        //     'submenu--open'
                        //   ),
                        //     e.currentTarget.classList.remove('hovered')
                        // }}
                      >
                        {item.title}
                      </Link>

                      {item.children.length > 0 && (
                        <div
                          className="submenu"
                          onMouseEnter={(e) => {
                            ;(document.documentElement.classList.add('submenu--open'),
                              document.querySelector('.hovered')?.classList.remove('hovered'),
                              e.currentTarget.previousElementSibling !== null &&
                                e.currentTarget.previousElementSibling.classList.add('hovered'))
                          }}
                          // onMouseLeave={e => {
                          //   document.documentElement.classList.remove(
                          //     'submenu--open'
                          //   ),
                          //     document
                          //       .querySelector('.hovered')
                          //       ?.classList.remove('hovered')
                          // }}
                        >
                          <div className="submenu__inner container--l gutter--m spacing--m">
                            <p className="submenu__title title--s">{item.title}</p>

                            <ul className="submenu__list">
                              {item.children.map((child) => (
                                <li className="submenu__item" key={`${child.url}-${child.title}`}>
                                  <Link
                                    url={child.url}
                                    className={`submenu__link${isLinkActive(child.url) ? ' active' : ''}`}
                                  >
                                    {child.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                  {lastButton && (
                    <li className="menu__item menu__item-highlighted" key={`${lastButton.url}-${lastButton.title}`}>
                      <Link
                        url={lastButton.url}
                        className={`button button--primary ${
                          isLinkActive(lastButton.url) || isSubMenuActive(lastButton) ? ' active' : ''
                        }`}
                        onMouseEnter={(e) =>
                          lastButton.children.length > 0
                            ? (document.documentElement.classList.add('submenu--open'),
                              document.querySelector('.hovered')?.classList.remove('hovered'),
                              e.currentTarget.classList.add('hovered'))
                            : (document.documentElement.classList.remove('submenu--open'),
                              document.querySelector('.hovered')?.classList.remove('hovered'))
                        }
                      >
                        {lastButton.title}
                      </Link>

                      {lastButton.children.length > 0 && (
                        <div
                          className="submenu"
                          onMouseEnter={(e) => {
                            ;(document.documentElement.classList.add('submenu--open'),
                              document.querySelector('.hovered')?.classList.remove('hovered'),
                              e.currentTarget.previousElementSibling !== null &&
                                e.currentTarget.previousElementSibling.classList.add('hovered'))
                          }}
                        >
                          <div className="submenu__inner container--l gutter--m spacing--m">
                            <p className="submenu__title title--s">{lastButton.title}</p>

                            <ul className="submenu__list">
                              {lastButton.children.map((child) => (
                                <li className="submenu__item" key={`${child.url}-${child.title}`}>
                                  <Link
                                    url={child.url}
                                    className={`submenu__link${isLinkActive(child.url) ? ' active' : ''}`}
                                  >
                                    {child.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </li>
                  )}
                </ul>

                <div className="menu__options">
                  <Link
                    className="search-toggler"
                    url="/zoeken"
                    onMouseEnter={() => {
                      ;(document.documentElement.classList.remove('submenu--open'),
                        document.querySelector('.hovered')?.classList.remove('hovered'))
                    }}
                    onClick={(e) => toggleMobileMenu(document.querySelector('#a11m-mmt'), 'remove')}
                  >
                    <span className="sr-text">Search</span>
                    <svg className="button__icon toggler__icon" width="18px" height="18px">
                      <use xlinkHref="#icon--search" />
                    </svg>
                  </Link>

                  <button
                    id="a11m-mmt"
                    className="menu__toggler menu-toggler"
                    onClick={(e) => toggleMobileMenu(e.currentTarget)}
                    aria-controls="a11y-mm"
                    aria-expanded={false}
                  >
                    <span className="sr-text">Toggle menu</span>
                    <div className="toggler__icon" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                  </button>
                </div>
              </nav>
            </div>
          </div>

          <div className="utilities text--s">
            <div className="utilities__inner container--l gutter--m">
              <nav className="menu menu--contact" aria-label="Contact menu">
                <ul className="menu__list">
                  <li className="menu__item">
                    <a className="menu__link" href="tel:+31793467346">
                      079 346 7346
                    </a>
                  </li>

                  <li className="menu__item">
                    <a className="menu__link" href="mailto:info@evofenedex.nl">
                      info@evofenedex.nl
                    </a>
                  </li>
                </ul>
              </nav>

              <nav className="menu menu--utility" aria-label="Utility menu">
                <ul className="menu__list">
                  {utilityItems.map((item) => (
                    <li className="menu__item" key={`${item.url}-${item.title}`}>
                      <Link url={item.url} className={`menu__link${isLinkActive(item.url) ? ' active' : ''}`}>
                        {item.title}
                      </Link>
                    </li>
                  ))}
                  <li className="menu__item" key={`translation-option`}>
                    <div className="menu__link translate-select">
                      <svg className="translate-select__icon" aria-hidden="true" width="16px" height="16px">
                        <use xlinkHref="#icon--globe" />
                      </svg>
                      <span className="translate-select__label">Languages</span>
                      <svg className="translate-select__icon translate-select__icon--chevron" aria-hidden="true" width="12px" height="8px">
                        <use xlinkHref="#icon--chevron" />
                      </svg>
                      <select
                        className="translate-select__native"
                        value=""
                        onChange={(e) => selectLanguage(e.target.value)}
                        aria-label="Languages"
                      >
                        <option value="" disabled>Languages</option>
                        {recommendedLanguages.map((lang) => (
                          <option key={lang.code} value={lang.code}>{lang.label}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <nav id="a11y-mm" className="mobile-menu" aria-label="Mobile navigation" aria-hidden="true">
            <div className="menu menu--mobile-main title--s">
              <ul className="menu__list container--l gutter--m">
                {items.map((item) => (
                  <li className="menu__item" key={`${item.url}-${item.title}`}>
                    <Link
                      url={item.url}
                      className={`menu__link${isLinkActive(item.url) || isSubMenuActive(item) ? ' active' : ''}`}
                      onClick={(e) => toggleMobileMenu(document.querySelector('#a11m-mmt'), 'remove')}
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="menu menu--mobile-utility text--s">
              <ul className="menu__list container--l gutter--m">
                {utilityItems.map((item) => (
                  <li className="menu__item" key={`${item.url}-${item.title}`}>
                    <Link
                      url={item.url}
                      className={`menu__link${isLinkActive(item.url) ? ' active' : ''}`}
                      onClick={(e) => toggleMobileMenu(document.querySelector('#a11m-mmt'), 'remove')}
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
                 <li className="menu__item" key={`translation-option`}>
                    <div className="menu__link translate-select">
                      <svg className="translate-select__icon" aria-hidden="true" width="16px" height="16px">
                        <use xlinkHref="#icon--globe" />
                      </svg>
                      <span className="translate-select__label">Translate</span>
                      <svg className="translate-select__icon translate-select__icon--chevron" aria-hidden="true" width="12px" height="8px">
                        <use xlinkHref="#icon--chevron" />
                      </svg>
                      <select
                        className="translate-select__native"
                        value=""
                        onChange={(e) => selectLanguage(e.target.value)}
                        aria-label="Translate"
                      >
                        <option value="" disabled>Translate</option>
                        {recommendedLanguages.map((lang) => (
                          <option key={lang.code} value={lang.code}>{lang.label}</option>
                        ))}
                      </select>
                    </div>
                  </li>
              </ul>
            </div>
          </nav>
        </div>
        {props.translateState.isTranslateBannerVisible && (
          <TranslateBanner
            onClose={() => {
              props.closeTranslateBanner()
              setTranslationBannerCookie('no')
            }}
          />
        )}
        <div id="google_translate_element" style={{ display: 'none' }} />
      </header>
    </>
  )
}

const toggleMobileMenu = (currentTarget: HTMLElement | null, forceState?: 'remove') => {
  if (currentTarget === null) return

  const docEl = document.documentElement
  const menuEl = document.querySelector('#a11y-mm')

  if (docEl.classList.contains('menu--open') || (forceState && forceState === 'remove')) {
    ;(docEl.classList.remove('menu--open'),
      currentTarget.setAttribute('aria-expanded', 'false'),
      menuEl?.setAttribute('aria-hidden', 'true'))
  } else {
    ;(docEl.classList.add('menu--open'),
      currentTarget.setAttribute('aria-expanded', 'true'),
      menuEl?.setAttribute('aria-hidden', 'false'))
  }
}
